import React, { useState } from "react";
import { slipRepo, storageService } from "../../services";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { resolveBranchId } from "../../services/commerce";
import { useMockCustomer } from "./useMockCustomer";
import { Button, Card, EmptyState, Input, Select, StatusBadge, UploadBox, baht, formatDate, useAsync } from "../../components/ui";

const BANKS = ["SCB", "KBANK", "BBL", "KTB", "TTB", "BAY"];

export default function SlipUpload() {
  const { orgId, branchId, branches, toast, checkStorage } = useApp();
  const { tx } = useI18n();
  const { customer } = useMockCustomer(orgId);
  const { data: slips, reload } = useAsync(
    () => (customer ? slipRepo.all(orgId, (row) => row.customerId === customer.id) : Promise.resolve([])),
    [orgId, customer],
    []
  );

  const [preview, setPreview] = useState(null);
  const [fileSize, setFileSize] = useState(0);
  const [form, setForm] = useState({ amount: "", bank: BANKS[0] });

  const submit = async () => {
    if (!preview || !form.amount) return toast(tx("อัปโหลดสลิปและกรอกจำนวนเงิน", "Upload slip and enter amount"), "error");
    // บล็อกถ้าเกินโควตาที่เก็บไฟล์ของร้าน (อ่านยอดล่าสุดทุกครั้ง)
    try {
      if (!checkStorage(await storageService.usedBytes(orgId), fileSize).ok) {
        return toast(tx("พื้นที่เก็บไฟล์ของร้านเต็มแล้ว ไม่สามารถส่งสลิปได้ กรุณาติดต่อร้าน", "The shop's storage is full — please contact the shop"), "error");
      }
    } catch (err) {
      return toast(err.message, "error");
    }
    await slipRepo.create(orgId, {
      customerId: customer.id,
      branchId: resolveBranchId(branches, branchId) || undefined,
      customerName: customer.name,
      amount: Number(form.amount),
      bank: form.bank,
      transferAt: new Date().toISOString(),
      status: "pending",
      channel: "app",
      sizeBytes: fileSize,
    });
    setPreview(null);
    setFileSize(0);
    setForm({ amount: "", bank: BANKS[0] });
    reload();
    toast(tx("ส่งสลิปเรียบร้อย รอเจ้าหน้าที่ตรวจสอบ", "Slip submitted, pending review"), "success");
  };

  const onFile = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      setPreview(reader.result);
      setFileSize(file.size);
    };
    reader.readAsDataURL(file);
  };

  return (
    <>
      <Card title={tx("แจ้งโอนเงิน", "Report a transfer")}>
        <UploadBox onFile={onFile} preview={preview} hint={tx("ถ่ายรูปหรืออัปโหลดสลิปโอนเงิน", "Take a photo or upload your slip")} />
        <div className="u-mt-16">
          <Input label={tx("จำนวนเงิน", "Amount")} type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          <Select label={tx("ธนาคาร", "Bank")} options={BANKS} value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} />
          <Button variant="primary" block onClick={submit}>{tx("ส่งสลิป", "Submit slip")}</Button>
        </div>
      </Card>

      <Card title={tx("ประวัติการแจ้งโอน", "Submission history")}>
        {!slips?.length ? (
          <EmptyState text={tx("ยังไม่มีการแจ้งโอนเงิน", "No submissions yet")} />
        ) : (
          slips.map((row) => (
            <div key={row.id} className="u-between" style={{ padding: "9px 0", borderBottom: "1px solid var(--c-slate-100)" }}>
              <div>
                <div className="u-strong u-fs-13">{row.bank} · {baht(row.amount)}</div>
                <div className="u-fs-12 u-muted">{formatDate(row.transferAt, true)}</div>
              </div>
              <StatusBadge status={row.status} />
            </div>
          ))
        )}
      </Card>
    </>
  );
}
